import { getAsset, getFlight } from "../offline/store";
import type { Annotations, FlightDetailResponse, FlightSetDetail } from "../api/types";

export interface OfflineDetail {
  detail: FlightDetailResponse;
  annotations: Annotations | null;
  /** Object URL for the cached track.json — caller must revokeObjectURL on unmount. */
  trackObjectUrl: string;
}

/**
 * Rebuild the ViewerPage's `FlightDetailResponse` purely from IndexedDB, for
 * when `GET /api/flights/:id` is unreachable (offline, Worker down).
 *
 * Returns `null` whenever the cache cannot furnish a mountable viewer — no
 * record, a partial (cancelled/failed) download, or a missing track blob —
 * so the caller can fall back to surfacing the original network error.
 *
 * The annotations baseline is best-effort: a missing or corrupt one yields
 * `annotations: null`, which the AnnotationStore treats exactly like an empty
 * server document.
 */
export async function buildOfflineDetail(flightId: string): Promise<OfflineDetail | null> {
  const record = await getFlight(flightId);
  if (record === null || record.partial === true) return null;

  const trackRecord = await getAsset(`${flightId}:track`);
  if (trackRecord === null) return null;
  const trackObjectUrl = URL.createObjectURL(trackRecord.blob);

  let annotations: Annotations | null = null;
  const baseline = await getAsset(`${flightId}:annotations-baseline`);
  if (baseline !== null) {
    try {
      annotations = JSON.parse(await baseline.blob.text()) as Annotations;
    } catch {
      // Corrupt baseline — start from an empty document rather than refusing
      // to open a flight whose video and track are both cached.
      annotations = null;
    }
  }

  // The set block is best-effort too. Its members' `trackUrl`s were blanked
  // before caching (they are short-lived presigns); the ghost polylines come
  // from the `:set-track:*` blobs via loadCachedGhostTracks instead.
  let set: FlightSetDetail | undefined;
  const setRecord = await getAsset(`${flightId}:set`);
  if (setRecord !== null) {
    try {
      const parsed = JSON.parse(await setRecord.blob.text()) as unknown;
      // Shape-check, not just parse-check. `JSON.parse` succeeds for "null",
      // "{}", "[]" and "7", and every one of those would land on
      // `detail.set` as a memberless value. ViewerPage's ghost effect then
      // reads `detailSet.members.some(...)` SYNCHRONOUSLY in the effect body,
      // where the promise `.catch` cannot help — a blank viewer instead of a
      // degrade. Only an object carrying an array of members is a set.
      if (
        typeof parsed === "object" && parsed !== null
        && Array.isArray((parsed as { members?: unknown }).members)
      ) {
        set = parsed as FlightSetDetail;
      }
    } catch {
      // Corrupt cached set block — open the flight without set navigation
      // rather than refusing it.
      set = undefined;
    }
  }

  return {
    // `urls.meta` / `urls.thumb` are empty strings only to satisfy the type:
    // the only readers are the offline-download builders (offline/refreshFlight.ts
    // and offline/download.ts), which need the network anyway. Nothing on the
    // rendering/playback path fetches them — verified by grep for `urls.meta` /
    // `urls.thumb`. `urls.video` stays absent: the cached-video effect swaps in
    // a virtual URL, and there is no presigned URL to fall back to offline.
    detail: {
      flight: record.meta,
      urls: { meta: "", thumb: "", track: trackObjectUrl },
      ...(set !== undefined ? { set } : {}),
    },
    annotations,
    trackObjectUrl,
  };
}
